import React from 'react'
import { Link } from 'react-router-dom'
import ServeReportComponent from './ServeReportComponent'
import type { CheckStatus, CiStatus, EnvSummary, HealthResult } from '../utils/useDashboardData'
import type { Env } from '../contexts/AppsContext'

export const StatusRow: React.FC<{
  label: string
  status: CheckStatus
  detail: string
  title?: string
  link?: string
}> = ({ label, status, detail, title, link }) => (
  <div className="health-row" title={title}>
    <span className={`health-circle health-circle--${status}`} aria-hidden="true" />
    <span className="health-row__label">{label}</span>
    {link ? (
      <a className="health-row__detail health-row__detail--link" href={link} target="_blank" rel="noreferrer">
        {detail}
      </a>
    ) : (
      <span className="health-row__detail">{detail}</span>
    )}
  </div>
)

const healthDetail = (result?: HealthResult): string => {
  if (!result || result.status === 'unknown') return 'Not checked yet'

  if (result.status === 'up') {
    const latency = result.latencyMs !== undefined ? `, ${result.latencyMs}ms` : ''
    return `Responding (${result.statusCode}${latency})`
  }

  if (result.error) return `Unreachable - ${result.error}`
  return `Responding with error (${result.statusCode ?? 'no response'})`
}

const HealthRow: React.FC<{ label: string; result?: HealthResult }> = ({ label, result }) => (
  <StatusRow label={label} status={result?.status ?? 'unknown'} detail={healthDetail(result)} />
)

export const checkedAtLabel = (checkedAt?: string): string | null =>
  checkedAt ? `Checked: ${new Date(checkedAt).toLocaleString()}` : null

const ciBadgeClass = (status: string, conclusion: string | null): string => {
  if (status === 'in_progress' || status === 'queued') return 'ci-badge--running'
  if (conclusion === 'success') return 'ci-badge--success'
  if (conclusion === 'failure' || conclusion === 'timed_out' || conclusion === 'cancelled') return 'ci-badge--failure'
  return 'ci-badge--unknown'
}

const ciBadgeText = (status: string, conclusion: string | null): string => {
  if (status === 'in_progress') return 'Running'
  if (status === 'queued') return 'Queued'
  if (conclusion) return conclusion
  return 'Unknown'
}

interface StatusCardProps {
  label: string
  // Where the title links (the app's report list)
  to: string
  // Omitted for single-environment apps - no badge
  env?: Env
  envSummary: EnvSummary
  fe?: HealthResult
  be?: HealthResult
  showFe: boolean
  showBe: boolean
  ci?: CiStatus
  // Additional Services: stacked single-column layout, stats on one line
  compact?: boolean
}

const StatusCard: React.FC<StatusCardProps> = ({ label, to, env, envSummary, fe, be, showFe, showBe, ci, compact }) => {
  const hasHealth = showFe || showBe
  const classes = ['dashboard-card', compact && 'dashboard-card--compact', !hasHealth && 'dashboard-card--no-health']

  return (
    <div className={classes.filter(Boolean).join(' ')}>
      <div className="dashboard-card__header">
        <h4>
          <Link to={to} className="dashboard-card__title-link">
            {label}
          </Link>
          {env && <span className={`env-badge env-badge--${env}`}>{env}</span>}
        </h4>
        {ci && (
          <a
            href={ci.runUrl ?? undefined}
            target="_blank"
            rel="noreferrer"
            className={`ci-badge ${ciBadgeClass(ci.status, ci.conclusion)}`}
          >
            {ciBadgeText(ci.status, ci.conclusion)}
          </a>
        )}
      </div>

      <div className="dashboard-card__summary">
        <div className="dashboard-card__passrate-block">
          <div className="dashboard-card__passrate">
            {envSummary.passRate !== null ? `${envSummary.passRate}%` : 'N/A'}
          </div>
          <div className="dashboard-card__passrate-label">latest run pass rate</div>
          {envSummary.reportName && envSummary.hasPlaywright && (
            <ServeReportComponent
              reportName={envSummary.reportName}
              reportType="playwright"
              label="Report"
              className="dashboard-card__view-report"
            />
          )}
        </div>

        {envSummary.summary && (
          <div className="dashboard-card__stats">
            <span>{envSummary.summary.passed} passed</span>
            <span>{envSummary.summary.failed} failed</span>
            <span>{envSummary.summary.flaky} flaky</span>
            <span>{envSummary.summary.skipped} skipped</span>
          </div>
        )}
      </div>

      {hasHealth && (
        <div className="dashboard-card__health-block">
          <div className="dashboard-card__health">
            {showFe && <HealthRow label="Frontend" result={fe} />}
            {showBe && <HealthRow label="Backend" result={be} />}
          </div>
          <div className="dashboard-card__checked-at">{checkedAtLabel(fe?.checkedAt ?? be?.checkedAt)}</div>
        </div>
      )}

      <div className="dashboard-card__footer">
        {envSummary.lastUpdated ? `Last run: ${new Date(envSummary.lastUpdated).toLocaleString()}` : 'No runs yet'}
      </div>
    </div>
  )
}

export default StatusCard
